/**
 * Timing — when a component starts working (the `start` gate) and polling that behaves.
 * Every function takes the Lifetime that owns it; nothing here outlives that lifetime.
 * @typedef {import('./lifetime.js').Lifetime} Lifetime
 */

/**
 * Run fn once, when the element first comes near the viewport (200 px look-ahead).
 * @param {Lifetime} lifetime
 * @param {Element} element
 * @param {() => void} fn
 * @param {{ rootMargin?: string }} [options]
 */
export function whenVisible(lifetime, element, fn, { rootMargin = '200px 0px' } = {}) {
  if (!lifetime.active) return
  if (typeof IntersectionObserver !== 'function') return void fn()
  const observer = new IntersectionObserver(
    (entries) => {
      if (!lifetime.active || !entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      fn()
    },
    { rootMargin },
  )
  lifetime.observe(observer, element)
}

/**
 * Run fn once, when the main thread is idle (or after `timeout` ms at the latest).
 * @param {Lifetime} lifetime
 * @param {() => void} fn
 * @param {{ timeout?: number }} [options]
 */
export function whenIdle(lifetime, fn, { timeout = 2000 } = {}) {
  if (!lifetime.active) return
  if (typeof requestIdleCallback !== 'function') return void lifetime.timeout(fn, 1)
  const id = requestIdleCallback(
    () => {
      forget()
      fn()
    },
    { timeout },
  )
  const forget = lifetime.add(() => cancelIdleCallback(id))
}

/**
 * Run fn once, on the first sign that the user is going to use the element.
 * @param {Lifetime} lifetime
 * @param {Element} element
 * @param {() => void} fn
 * @param {{ events?: string[] }} [options]
 */
export function onFirstInteraction(lifetime, element, fn, { events = ['pointerenter', 'focusin', 'touchstart'] } = {}) {
  const once = lifetime.child()
  for (const type of events) {
    once.listen(
      element,
      type,
      () => {
        once.dispose()
        fn()
      },
      { passive: true },
    )
  }
}

/**
 * The `start` gate: `connect` | `visible` | `idle` | `interaction`. Used by BaseController.
 * @param {Lifetime} lifetime
 * @param {Element} element
 * @param {'connect' | 'visible' | 'idle' | 'interaction'} mode
 * @param {() => void} fn
 */
export function startWhen(lifetime, element, mode, fn) {
  if (mode === 'visible') whenVisible(lifetime, element, fn)
  else if (mode === 'idle') whenIdle(lifetime, fn)
  else if (mode === 'interaction') onFirstInteraction(lifetime, element, fn)
  else if (lifetime.active) fn()
}

const MIN_EVERY = 1000
/** Error kinds that end a poll for good: retrying cannot help (or is not allowed). */
const STOP_KINDS = new Set(['session', 'forbidden', 'config', 'auth-check'])

/**
 * Poll `fn(signal, url)` every `every` ms.
 *  - recursive timeout after the previous run settles: never overlaps
 *  - paused (no timer) and the in-flight run aborted while document.hidden; one catch-up run when visible
 *  - failure (throw, `false`, `{ ok: false, error }`) multiplies the delay by 2 up to `maxBackoff`, with jitter
 *  - STOPS for good on error kind session / forbidden / config / auth-check
 *  - refuses an empty URL (when `url` is given): it stops, and refresh() restarts it
 *  - `every` has a floor of one second
 * `fn` returns an http result (`{ ok }`), a boolean, or nothing (= success). A `stale` result is ignored.
 * @param {Lifetime} lifetime
 * @param {(signal: AbortSignal, url: string) => unknown} fn
 * @param {{ every: number, maxBackoff?: number, url?: string | (() => string), immediate?: boolean }} options
 * @returns {{ stop: () => void, refresh: () => void }}
 */
export function poll(lifetime, fn, { every, maxBackoff, url, immediate = true }) {
  if (!Number.isFinite(every)) throw new TypeError('poll: `every` must be a number of milliseconds')
  every = Math.max(MIN_EVERY, every)
  maxBackoff = Math.max(every, maxBackoff ?? every * 8)

  const own = lifetime.child()
  let delay = every
  let running = false
  let due = false
  /** @type {(() => void) | null} */
  let cancel = null
  /** @type {AbortController | null} */
  let current = null

  const schedule = (ms) => {
    cancel?.()
    cancel = own.timeout(tick, ms)
  }

  async function tick() {
    cancel = null
    if (!own.active || running) return
    const target = url === undefined ? '' : (typeof url === 'function' ? url() : url) || ''
    if (url !== undefined && !target) return // refuses an empty URL
    if (document.hidden) return void (due = true)
    running = true
    const run = new AbortController()
    current = run
    const forgetRun = own.add(() => run.abort())
    let outcome = 'ok'
    try {
      const result = await fn(run.signal, target)
      if (result?.stale || run.signal.aborted) outcome = 'ignored'
      else if (result === false || result?.ok === false) {
        outcome = STOP_KINDS.has(result?.error?.kind) ? 'stop' : 'failed'
      }
    } catch {
      outcome = run.signal.aborted ? 'ignored' : 'failed'
    } finally {
      running = false
      current = null
      forgetRun()
    }
    if (!own.active) return
    if (outcome === 'stop') return void own.dispose()
    if (outcome === 'ignored') {
      if (!document.hidden) schedule(delay) // superseded run: try again at the current pace
      return
    }
    if (outcome === 'ok') {
      delay = every
      schedule(delay)
    } else {
      delay = Math.min(delay * 2, maxBackoff)
      schedule(Math.max(every, delay * (0.5 + Math.random() * 0.5))) // jitter: 50-100 % of the delay
    }
  }

  own.listen(document, 'visibilitychange', () => {
    if (document.hidden) {
      current?.abort()
      if (cancel) {
        cancel()
        cancel = null
      }
      due = true
    } else if (due) {
      due = false
      tick()
    }
  })

  if (immediate) tick()
  else schedule(every)

  return {
    stop: () => own.dispose(),
    refresh: () => {
      delay = every
      cancel?.()
      tick()
    },
  }
}
